/**
 * Event bands drawn under the Cesium timeline (handover 7.1.3).
 *
 * A canvas strip per category: ADCS modes, illumination, reboots, data gaps, clock jumps. This is
 * where an operator sees *why* a window looks the way it does before reading a single number -
 * and where the "data gaps must be visually honest" requirement is discharged, since a gap is a
 * hatched band rather than an absence of ink.
 */

import type { TimelineEvent } from '../api/types';

export interface BandRow {
  category: string;
  label: string;
  events: TimelineEvent[];
}

const CATEGORY_ORDER = [
  'adcs_ctrl_mode',
  'adcs_op_mode',
  'adcs_est_mode',
  'adcs_run_mode',
  'EPS_state',
  'ADCS_state',
  'GNSS_state',
  'CLK_TREE_state',
  'GNSS_ETH_state',
  'Payload_state',
  'illumination',
  'boot',
  'wheels',
  'telemetry_source',
  'estimator',
  'data_quality',
  'element_set',
];

const CATEGORY_LABELS: Record<string, string> = {
  adcs_ctrl_mode: 'Control mode',
  adcs_op_mode: 'Op mode',
  adcs_est_mode: 'Estimator',
  adcs_run_mode: 'Run mode',
  EPS_state: 'EPS',
  ADCS_state: 'ADCS subsys',
  GNSS_state: 'GNSS',
  CLK_TREE_state: 'Clock tree',
  GNSS_ETH_state: 'GNSS eth',
  Payload_state: 'Payload',
  illumination: 'Illumination',
  boot: 'Reboots',
  wheels: 'Wheels',
  telemetry_source: 'TM source',
  estimator: 'Data gaps',
  data_quality: 'Clock',
  element_set: 'Element set',
};

const LABEL_COLOURS: Record<string, string> = {
  sunlit: '#3d4f2f',
  penumbra: '#5a4a20',
  umbra: '#1d2434',
  DETUMBLE: '#7a3f2a',
  Y_MOMENTUM: '#2a5a4a',
  XYZ_WHEEL: '#2a4a6a',
  NONE: '#33383f',
  IDLE: '#33383f',
  NOMINAL: '#2a5a4a',
  EKF: '#2a4a6a',
  ENABLED: '#2a5a4a',
  OFF: '#33383f',
  // Telemetry provenance: stored whole-orbit data (re-timestamped onto the onboard clock)
  // versus samples seen live during a pass.
  WOD: '#5a4a8a',
  live: '#3f6a33',
  // Subsystem run states (subsys_status_p enum labels).
  OPERATIONAL: '#2a5a4a',
  POWER_UP: '#5a4a20',
  STOPPED: '#33383f',
};

const SEVERITY_COLOURS: Record<string, string> = {
  info: '#4c78a8',
  warning: '#d99a2b',
  error: '#d9534f',
};

const ROW_HEIGHT = 17;
const ROW_GAP = 3;
const LABEL_WIDTH = 104;

export interface EventBandsOptions {
  container: HTMLElement;
  onSeek: (epochMs: number) => void;
  onHover?: (event: TimelineEvent | null) => void;
}

export class EventBands {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly tooltip: HTMLElement;
  private rows: BandRow[] = [];
  private windowStart = 0;
  private windowStop = 1;
  private playheadMs: number | null = null;

  constructor(private readonly options: EventBandsOptions) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'event-bands';
    options.container.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;

    this.tooltip = document.createElement('div');
    this.tooltip.className = 'event-tooltip';
    this.tooltip.hidden = true;
    options.container.appendChild(this.tooltip);

    this.buildLegend();

    this.canvas.addEventListener('click', (e) => {
      const time = this.timeAt(e.offsetX);
      if (time !== null) options.onSeek(time);
    });
    this.canvas.addEventListener('mousemove', (e) => this.handleHover(e));
    this.canvas.addEventListener('mouseleave', () => {
      this.tooltip.hidden = true;
      options.onHover?.(null);
    });
  }

  setWindow(startMs: number, stopMs: number): void {
    this.windowStart = startMs;
    this.windowStop = Math.max(stopMs, startMs + 1);
    this.draw();
  }

  setEvents(events: TimelineEvent[]): void {
    const grouped = new Map<string, TimelineEvent[]>();
    for (const event of events) {
      const list = grouped.get(event.category) ?? [];
      list.push(event);
      grouped.set(event.category, list);
    }
    const known = CATEGORY_ORDER.filter((c) => grouped.has(c));
    const extra = [...grouped.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
    this.rows = [...known, ...extra].map((category) => ({
      category,
      label: CATEGORY_LABELS[category] ?? category,
      events: grouped.get(category) ?? [],
    }));
    this.resize();
  }

  setPlayhead(epochMs: number): void {
    this.playheadMs = epochMs;
    this.draw();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio, 2);
    const width = Math.max(this.options.container.clientWidth, 320);
    const height = Math.max(this.rows.length * (ROW_HEIGHT + ROW_GAP) + 6, 24);
    this.canvas.width = Math.floor(width * dpr);
    this.canvas.height = Math.floor(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  }

  /**
   * A static legend for everything the bands can draw, built from the same colour tables the
   * canvas uses so the two can never drift apart. Collapsed by default; the colours it
   * documents are the ones in LABEL_COLOURS and SEVERITY_COLOURS above.
   */
  private buildLegend(): void {
    const details = document.createElement('details');
    details.className = 'bands-legend';
    const summary = document.createElement('summary');
    summary.textContent = 'Legend';
    details.appendChild(summary);
    const grid = document.createElement('div');
    grid.className = 'bands-legend-grid';

    const item = (label: string, colour?: string, hatch = false): void => {
      const el = document.createElement('span');
      el.className = 'bands-legend-item';
      const swatch = document.createElement('i');
      if (hatch) {
        swatch.className = 'swatch-hatch';
      } else if (colour) {
        swatch.style.background = colour;
      }
      el.appendChild(swatch);
      el.appendChild(document.createTextNode(label));
      grid.appendChild(el);
    };

    item('DETUMBLE', LABEL_COLOURS.DETUMBLE);
    item('Y_MOMENTUM / NOMINAL / ENABLED', LABEL_COLOURS.Y_MOMENTUM);
    item('XYZ_WHEEL / EKF', LABEL_COLOURS.XYZ_WHEEL);
    item('IDLE / OFF / NONE', LABEL_COLOURS.IDLE);
    item('Sunlit', LABEL_COLOURS.sunlit);
    item('Penumbra', LABEL_COLOURS.penumbra);
    item('Umbra', LABEL_COLOURS.umbra);
    item('WOD (stored, onboard-clock time)', LABEL_COLOURS.WOD);
    item('Live downlink', LABEL_COLOURS.live);
    item('Subsystem OPERATIONAL', LABEL_COLOURS.OPERATIONAL);
    item('Subsystem POWER_UP', LABEL_COLOURS.POWER_UP);
    item('Subsystem STOPPED', LABEL_COLOURS.STOPPED);
    item('Element set in use', SEVERITY_COLOURS.info);
    item('Info event', SEVERITY_COLOURS.info);
    item('Warning (reboots, clock drift, stale TLE)', SEVERITY_COLOURS.warning);
    item('Error event', SEVERITY_COLOURS.error);
    item('Data gap', undefined, true);
    item('Playhead', '#ffc857');

    details.appendChild(grid);
    this.options.container.appendChild(details);
  }

  private xFor(epochMs: number): number {
    const width = this.canvas.clientWidth - LABEL_WIDTH;
    const fraction = (epochMs - this.windowStart) / (this.windowStop - this.windowStart);
    return LABEL_WIDTH + fraction * width;
  }

  private timeAt(x: number): number | null {
    const width = this.canvas.clientWidth - LABEL_WIDTH;
    if (width <= 0 || x < LABEL_WIDTH) return null;
    return this.windowStart + ((x - LABEL_WIDTH) / width) * (this.windowStop - this.windowStart);
  }

  private draw(): void {
    const ctx = this.ctx;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    ctx.clearRect(0, 0, width, height);

    this.rows.forEach((row, index) => {
      const y = 3 + index * (ROW_HEIGHT + ROW_GAP);

      ctx.fillStyle = '#12181f';
      ctx.fillRect(LABEL_WIDTH, y, width - LABEL_WIDTH, ROW_HEIGHT);

      ctx.fillStyle = '#8b95a4';
      ctx.font = '10px Inter, system-ui, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText(row.label, 6, y + ROW_HEIGHT / 2, LABEL_WIDTH - 12);

      for (const event of row.events) {
        this.drawEvent(event, y);
      }
    });

    if (this.playheadMs !== null) {
      const x = this.xFor(this.playheadMs);
      if (x >= LABEL_WIDTH && x <= width) {
        ctx.strokeStyle = '#ffc857';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }
  }

  private drawEvent(event: TimelineEvent, y: number): void {
    const ctx = this.ctx;
    const start = Date.parse(event.time);
    const end = event.end_time ? Date.parse(event.end_time) : start;
    const x0 = this.xFor(start);
    const x1 = this.xFor(end);

    if (event.kind === 'gap') {
      // Hatched, not filled: a gap must not be mistakable for data.
      const w = Math.max(x1 - x0, 2);
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, y, w, ROW_HEIGHT);
      ctx.clip();
      ctx.fillStyle = '#241b1b';
      ctx.fillRect(x0, y, w, ROW_HEIGHT);
      ctx.strokeStyle = '#7a4a4a';
      ctx.lineWidth = 1;
      for (let x = x0 - ROW_HEIGHT; x < x0 + w + ROW_HEIGHT; x += 6) {
        ctx.beginPath();
        ctx.moveTo(x, y + ROW_HEIGHT);
        ctx.lineTo(x + ROW_HEIGHT, y);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    if (event.end_time && x1 - x0 > 1.5) {
      ctx.fillStyle = LABEL_COLOURS[event.label] ?? SEVERITY_COLOURS[event.severity] ?? '#2a3038';
      ctx.fillRect(x0, y, x1 - x0, ROW_HEIGHT);
      const label = event.label;
      if (x1 - x0 > 46) {
        ctx.fillStyle = '#d5dbe4';
        ctx.font = '9px Inter, system-ui, sans-serif';
        ctx.fillText(label, x0 + 4, y + ROW_HEIGHT / 2, x1 - x0 - 8);
      }
      return;
    }

    // Point event: a tick mark, minimum 2 px wide so a single reboot is never invisible. A
    // labelled colour (an isolated WOD or mode sample) wins over the severity colour so a
    // one-sample band keeps its row's colour language.
    ctx.fillStyle = LABEL_COLOURS[event.label] ?? SEVERITY_COLOURS[event.severity] ?? '#4c78a8';
    ctx.fillRect(x0 - 1, y, 2.5, ROW_HEIGHT);
  }

  private handleHover(e: MouseEvent): void {
    const rowIndex = Math.floor((e.offsetY - 3) / (ROW_HEIGHT + ROW_GAP));
    const row = this.rows[rowIndex];
    if (!row || e.offsetX < LABEL_WIDTH) {
      this.tooltip.hidden = true;
      this.options.onHover?.(null);
      return;
    }
    const time = this.timeAt(e.offsetX);
    if (time === null) return;

    const hit = row.events.find((event) => {
      const start = Date.parse(event.time);
      const end = event.end_time ? Date.parse(event.end_time) : start;
      const pad = ((this.windowStop - this.windowStart) / Math.max(this.canvas.clientWidth, 1)) * 3;
      return time >= start - pad && time <= end + pad;
    });

    if (!hit) {
      this.tooltip.hidden = true;
      this.options.onHover?.(null);
      return;
    }
    const duration = hit.duration_s ? ` · ${formatDuration(hit.duration_s)}` : '';
    // Some events carry an explanation worth more than their label - a clock offset means little
    // as "+29 min" and a great deal as "these samples are drawn 29 min from where they happened".
    const message = typeof hit.detail?.message === 'string' ? `\n${hit.detail.message}` : '';
    this.tooltip.textContent =
      `${hit.label}${duration} · ${hit.time.replace('T', ' ').replace('Z', '')}${message}`;
    this.tooltip.hidden = false;
    this.tooltip.style.left = `${Math.min(e.offsetX + 12, this.canvas.clientWidth - 220)}px`;
    this.tooltip.style.top = `${e.offsetY + 14}px`;
    this.options.onHover?.(hit);
  }
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)} s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`;
  return `${(seconds / 3600).toFixed(2)} h`;
}
